import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api, { API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { SealCheck, X, CheckCircle } from "@phosphor-icons/react";
import UserBadge from "@/components/UserBadge";

function resolveImg(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${API_BASE.replace(/\/api$/, "")}${u}`;
  return u;
}

function UserAvatar({ user, size = 40 }) {
  const src = resolveImg(user?.avatar_url);
  const letter = (user?.username || "?").slice(0, 1).toUpperCase();
  if (src) return <img src={src} alt={user?.username} style={{ width: size, height: size }} className="rounded-full object-cover shrink-0" />;
  return (
    <div style={{ width: size, height: size }} className="rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center font-black shrink-0">
      {letter}
    </div>
  );
}

export default function AdminVerification() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/admin/verification/requests?status=${status}`);
    setRows(data || []);
  };

  useEffect(() => { load(); }, [status]);

  const approve = async (r) => {
    setBusy(r.id);
    try {
      await api.post(`/admin/verification/requests/${r.id}/approve`);
      toast.success(`Approved @${r.user?.username}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };
  const reject = async (r) => {
    if (!window.confirm(`Reject verification request from @${r.user?.username}?`)) return;
    setBusy(r.id);
    try {
      await api.post(`/admin/verification/requests/${r.id}/reject`);
      toast.success("Request rejected");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white inline-flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
          <SealCheck size={28} weight="fill" className="text-blue-600" /> Verification requests
        </h1>
        <div className="inline-flex rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-1 text-xs font-bold">
          {["pending", "approved", "rejected"].map((s) => (
            <button
              key={s}
              data-testid={`ver-filter-${s}`}
              onClick={() => setStatus(s)}
              className={`px-3 py-1.5 rounded-full capitalize transition-colors duration-200 ${status === s ? "bg-blue-700 text-white" : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
            >{s}</button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-10 text-center text-slate-500">
          No {status} requests.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} data-testid={`ver-row-${r.id}`} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 flex items-start gap-4 flex-wrap">
              <Link to={`/u/${r.user?.id}`}><UserAvatar user={r.user} size={48} /></Link>
              <div className="flex-1 min-w-[220px]">
                <Link to={`/u/${r.user?.id}`} className="font-black text-slate-900 dark:text-white hover:underline inline-flex items-center gap-1">
                  @{r.user?.username}
                  <UserBadge user={r.user} size={14} />
                </Link>
                <div className="text-xs text-slate-500 dark:text-slate-400">{r.user?.email}</div>
                <div className="text-sm text-slate-800 dark:text-slate-100 mt-2 whitespace-pre-wrap">{r.reason}</div>
                {r.social_link && (
                  <a href={r.social_link} target="_blank" rel="noreferrer noopener" className="mt-1 inline-block text-sm text-blue-700 dark:text-blue-400 hover:underline break-all">{r.social_link}</a>
                )}
                <div className="text-[11px] text-slate-500 mt-2">Submitted {new Date(r.created_at).toLocaleString()}</div>
              </div>
              {status === "pending" && (
                <div className="flex gap-2 self-center">
                  <button
                    data-testid={`ver-approve-${r.id}`}
                    onClick={() => approve(r)}
                    disabled={busy === r.id}
                    className="inline-flex items-center gap-1 rounded-full bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white font-bold px-4 py-2 text-sm transition-colors duration-200"
                  >
                    <CheckCircle size={14} weight="fill" /> Approve
                  </button>
                  <button
                    data-testid={`ver-reject-${r.id}`}
                    onClick={() => reject(r)}
                    disabled={busy === r.id}
                    className="inline-flex items-center gap-1 rounded-full bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-bold px-4 py-2 text-sm transition-colors duration-200"
                  >
                    <X size={14} weight="bold" /> Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
